/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 *
 * Utility functions around everything related to (DOM) Nodes.
 */

// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

/**
 * Map of node type identifiers with their corresponding type constants
 *
 * @type {object}
 */
const NODE_TYPE = {
  ELEMENT_NODE               :  1,
  ATTRIBUTE_NODE             :  2,
  TEXT_NODE                  :  3,
  CDATA_SECTION_NODE         :  4,
  ENTITY_REFERENCE_NODE      :  5,
  ENTITY_NODE                :  6,
  PROCESSING_INSTRUCTION_NODE:  7,
  COMMENT_NODE               :  8,
  DOCUMENT_NODE              :  9,
  DOCUMENT_TYPE_NODE         : 10,
  DOCUMENT_FRAGMENT_NODE     : 11,
  NOTATION_NODE              : 12
};


// -----------------------------------------------------------------------------
// PUBLIC API
// -----------------------------------------------------------------------------

/**
 * Returns true if @node is a text node
 *
 * @param {Node} node DOM node
 * @return {Boolean} true if @node is a text node
 */
function isCharacterDataNode(node) {
  return Boolean(node) &&
    (
      node.nodeType === NODE_TYPE.TEXT_NODE ||
      node.nodeType === NODE_TYPE.COMMENT_NODE ||
      node.nodeType === NODE_TYPE.CDATA_SECTION_NODE
    );
}


/**
 * Returns true if @node is a document or a document fragment
 *
 * @param {Node} node DOM node
 * @return {Boolean} true if @node is a document or a document fragment
 */
function isDocumentNode(node) {
  return Boolean(node) &&
    (
      node.nodeType === NODE_TYPE.DOCUMENT_NODE ||
      node.nodeType === NODE_TYPE.DOCUMENT_FRAGMENT_NODE
    );
}


/**
 * Returns true if @node is a HTML element node
 *
 * @param {Node} node DOM node
 * @return {Boolean} true if @node is an element node
 */
const isElementNode = function isElementNode(node) {
  return node ? node.nodeType === NODE_TYPE.ELEMENT_NODE : false;
}


/**
 * Returns true if @node is either a HTML element or a text node
 *
 * @param {Node} node DOM node
 * @return {Boolean} true if @node is an element or text node
 */
const isElementOrTextNode = function isElementOrTextNode(node) {
  return node
    ? node.nodeType === NODE_TYPE.ELEMENT_NODE || node.nodeType === NODE_TYPE.TEXT_NODE
    : false;
}


/**
 * Returns true if @node is a "meta" node, i.e. it has a data attribute
 * "data-meta" defined
 *
 * @param {HTMLElement} node HTML element node
 * @return {Boolean} true if @node has attribute "data-meta" defined
 */
const isMetaNode = function isMetaNode(node) {
  return node?.hasAttribute && node.hasAttribute('data-meta');
};


/**
 * Returns true if @node is a text node
 *
 * @param {Node} node DOM node
 * @return {Boolean} true if @node is a text node
 */
const isTextNode = function isTextNode(node) {
  return node ? node.nodeType === NODE_TYPE.TEXT_NODE : false;
};


/**
 * Returns true if @node is a "meta" node with a least one sibling text node,
 * i.e. @node is *not* replacing a text node in the node list
 *
 * @param {HTMLElement} node HTML element node
 * @return {Boolean} true if @node has attribute "data-meta" defined _and_ has
 *                   at least one sibling text node
 */
const isNewMetaNode = function isNewMetaNode(node) {
  return isMetaNode(node) && (isTextNode(node.previousSibling) || isTextNode(node.nextSibling));
};


/**
 * Returns true if @node is a text node with a preceeding meta node,
 * i.e. the @node is a result of a text node being splitted by a meta node
 *
 * @param {HTMLElement} node HTML element node
 * @return {Boolean} true if @node is a text node with preceeding text and meta nodes
 */
const isSplittedTextNode = function isSplittedTextNode(node) {
  return isTextNode(node) && isMetaNode(node.previousSibling);
};


/**
 * Returns true if @node is a enumerable node for child sequences.
 * A node is enumerable if it is *not* an additional meta node or
 * it's not a text node created by insertion of a meta node
 *
 * @param {Node} node DOM node
 * @return {Boolean} true if @node is a enumerable node for child sequences
 */
const isEnumerableNode = function isEnumerableNode(node) {
  return !isNewMetaNode(node) && !isSplittedTextNode(node) && node.nodeType !== NODE_TYPE.PROCESSING_INSTRUCTION_NODE;
};


/**
 * Returns the first text node of @node containing at least one character
 *
 * @param {HTMLElement} node HTML element node
 * @return {TextNode} First text-node of @node with length > 0
 */
function findFirstTextNode(node) {
  var textNode;

  if (isTextNode(node) && node.length > 0) {
    return node;
  }
  node = node.firstChild;
  while (node) {
    if ((textNode = findFirstTextNode(node))) {
      return textNode;
    }
    node = node.nextSibling;
  }
}


/**
 * Returns the value of @node's "data-meta" attribute
 *
 * @param {HTMLElement} node HTML element node
 * @return {String} Value of @node's attribute "data-meta"
 */
function getMetaAttribute(node) {
  return node?.getAttribute && node.getAttribute('data-meta');
}


/**
 * Returns the position of a child node in relation to it's parent
 * node as a integer, i.e the n-th child starting from index 0
 *
 * @param {HTMLElement} node Child node
 * @return {Integer} Number of preceeding siblings, i.e. @node is the n-th child
 */
function getNodeIndex(node) {
  var idx = -1;

  while (node) {
    // enumerate only content nodes (non-meta):
    if (isEnumerableNode(node)) {
      idx++;
    }
    node = node.previousSibling;
  }
  return idx;
};


/**
 * Returns the path representation for @node starting from @node's content document
 *
 * @param {Node} node DOM node
 * @return {String} DOM Path (zero index based) to @node
 */
function getNodePath(node) {
  const path = [];

  while (node?.parentNode) {
    const index = Array.prototype.indexOf.call(node.parentNode.childNodes, node);
    path.unshift(index);
    node = node.parentNode;
  }

  return path.join("/");
}


/**
 * Returns the hierarchy path to @node as a list of integers, representing the
 * child indexes (1-based).
 *
 * @param {HTMLElement} node End point of the child sequence
 * @return {Array} Sequence of integers representing the child indexes of
 *                 the hierarchy path to node
 */
function getChildSequenceFromNode(node) {
  const childSequence = [];
  const rootNode = node?.ownerDocument.documentElement;
  
  while (node && node !== rootNode) {
    // reset child sequence if current node is a meta node:
    if (isMetaNode(node)) {
      childSequence.length = 0;
      if (node.previousSibling) {
        node = node.previousSibling;
      }
    }
    // insert the node index at the beginning of the array
    childSequence.unshift(Math.max(0, getNodeIndex(node)) + 1);
    node = node.parentNode;
  }
  childSequence.unshift(1); // prepend constant index "1" for the HTML root node

  return childSequence;
}


/**
 * Unwraps the DOM @node, i.e. lifts all child nodes of @node one level up,
 * and joins adjacent text nodes into a single node
 *
 * @param {HTMLElement} node DOM node to remove
 */
function unwrapNode(node) {
  var parentNode = node.parentNode;
  var childNode;

  // iterate over all child nodes, move them one level up as sibling before @node:
  while ((childNode = node.firstChild) !== null) {
    parentNode.insertBefore(childNode, node);
  }
  // remove the empty meta node from DOM:
  parentNode.removeChild(node);
  // normalize parent node, joining adjacent text nodes:
  parentNode.normalize();
}


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export {
  NODE_TYPE,

  findFirstTextNode,
  getChildSequenceFromNode,
  getMetaAttribute,
  getNodeIndex,
  getNodePath,
  isCharacterDataNode,
  isDocumentNode,
  isElementNode,
  isElementOrTextNode,
  isEnumerableNode,
  isMetaNode,
  isNewMetaNode,
  isSplittedTextNode,
  isTextNode,
  unwrapNode
};
